// src/features/audits/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { InputField, TextareaField } from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

// Zod schema for new Audit
const FormSchema = z.object({
  auditable_type: z.string().nullable().optional(),
  auditable_id: z.coerce.number().optional().nullable(),
  associated_type: z.string().nullable().optional(),
  associated_id: z.coerce.number().optional().nullable(),
  user_type: z.string().nullable().optional(),
  user_id: z.coerce.number().optional().nullable(),
  username: z.string().nullable().optional(),
  user_firstname: z.string().nullable().optional(),
  user_lastname: z.string().nullable().optional(),
  user_email: z.string().nullable().optional(),
  action: z.string().nullable().optional(),
  version: z.coerce.number().optional().nullable(),
  comment: z.string().nullable().optional(),
  remote_address: z.string().nullable().optional(),
  request_uuid: z.string().nullable().optional(),
  audited_changes: z.preprocess((val) => {
    if (typeof val === "object" && val !== null) {
      return JSON.stringify(val, null, 2);
    }
    if (typeof val === "string") {
      try {
        // Try to parse and re-stringify for formatting
        const parsed = JSON.parse(val);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return val;
      }
    }
    return val;
  }, z.string().nullable().optional()),
});

export function AuditsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("audits.title", { ns: "tables", defaultValue: "Audit" })}{" "}
                {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="auditable_type"
                    label={t(`${table_name}.columns.auditable_type`, {
                      ns: "tables",
                      defaultValue: "Auditable Type",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="auditable_id"
                    label={t(`${table_name}.columns.auditable_id`, {
                      ns: "tables",
                      defaultValue: "Auditable ID",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
                {/* Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="associated_type"
                    label={t(`${table_name}.columns.associated_type`, {
                      ns: "tables",
                      defaultValue: "Associated Type",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="associated_id"
                    label={t(`${table_name}.columns.associated_id`, {
                      ns: "tables",
                      defaultValue: "Associated ID",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
                {/* Row 3 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="user_type"
                    label={t(`${table_name}.columns.user_type`, {
                      ns: "tables",
                      defaultValue: "User Type",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="user_id"
                    label={t(`${table_name}.columns.user_id`, {
                      ns: "tables",
                      defaultValue: "User ID",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
                {/* Row 4 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="username"
                    label={t(`${table_name}.columns.username`, {
                      ns: "tables",
                      defaultValue: "Username",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="user_firstname"
                    label={t(`${table_name}.columns.user_firstname`, {
                      ns: "tables",
                      defaultValue: "First Name",
                    })}
                    control={form.control}
                  />
                </div>
                {/* Row 5 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="user_lastname"
                    label={t(`${table_name}.columns.user_lastname`, {
                      ns: "tables",
                      defaultValue: "Last Name",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="user_email"
                    label={t(`${table_name}.columns.user_email`, {
                      ns: "tables",
                      defaultValue: "Email",
                    })}
                    control={form.control}
                  />
                </div>
                {/* Row 6 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="action"
                    label={t(`${table_name}.columns.action`, {
                      ns: "tables",
                      defaultValue: "Action",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="version"
                    label={t(`${table_name}.columns.version`, {
                      ns: "tables",
                      defaultValue: "Version",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
                {/* Row 7 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="remote_address"
                    label={t(`${table_name}.columns.remote_address`, {
                      ns: "tables",
                      defaultValue: "Remote Address",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="request_uuid"
                    label={t(`${table_name}.columns.request_uuid`, {
                      ns: "tables",
                      defaultValue: "Request UUID",
                    })}
                    control={form.control}
                  />
                </div>
                {/* Full width comment */}
                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="comment"
                    label={t(`${table_name}.columns.comment`, {
                      ns: "tables",
                      defaultValue: "Comment",
                    })}
                    control={form.control}
                    placeholder={t("form.placeholders.comment")}
                  />
                </div>
                {/* Full width audited_changes as JSON */}
                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="audited_changes"
                    label={t(`${table_name}.columns.audited_changes`, {
                      ns: "tables",
                      defaultValue: "Audited Changes (JSON)",
                    })}
                    control={form.control}
                    placeholder='e.g. {"field":"old→new"}'
                  />
                </div>
              </CardBody>
              <CardFooter>
                <div className="flex justify-between items-center w-full">
                  <div className="text-sm text-muted-foreground">
                    Audit records are read-only and cannot be modified
                  </div>
                </div>
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, [
                  "auditable_id",
                  "associated_id",
                  "user_id",
                ])
              : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
